import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { trackPurchase } from '../components/MetaPixel';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  oneOfKind?: boolean;
  variantId?: string | null;
  slug: string;
  image: string;
}

const Success: React.FC = () => {
  const { clearCart } = useCart();
  const { consent } = useCookieConsent();
  const [orderProcessed, setOrderProcessed] = useState(false);
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');
  const payment_intent = searchParams.get('payment_intent');
  const orderId = session_id || payment_intent;
  const { t } = useTranslation();
  const { localePath } = useLocale();

  // Simplified: Stripe webhook handles email + Telegram + CAPI Purchase.
  // This page only: fetches order for display, fires browser Pixel, clears cart.
  useEffect(() => {
    const processOrder = async () => {
      if (!orderId) return;

      // Dedup: only fire Pixel once per order
      const pixelFiredKey = `pixel_fired_${orderId}`;
      if (localStorage.getItem(pixelFiredKey) === 'true') {
        setOrderProcessed(true);
        return;
      }
      localStorage.setItem(pixelFiredKey, 'true');

      try {
        const queryParam = session_id
          ? `session_id=${session_id}`
          : `payment_intent=${payment_intent}`;
        const response = await fetch(`/api/get-session?${queryParam}`);
        if (!response.ok) {
          setOrderProcessed(true);
          return;
        }

        const data = await response.json();
        const items = (data.items || []) as CartItem[];
        const subtotal = items.reduce(
          (sum: number, item: CartItem) => sum + (item.price * item.quantity), 0
        );
        const shippingCostEuros = data.shipping_rate?.amount / 100 || 0;
        const total = subtotal + shippingCostEuros;

        // Fire browser Pixel Purchase (deduped with webhook CAPI via session_id as eventID)
        trackPurchase(
          {
            content_ids: items.map((item: CartItem) => item.id),
            contents: items.map((item: CartItem) => ({ id: item.id, quantity: item.quantity })),
            value: total,
            currency: 'EUR',
          },
          consent.marketing,
          session_id || undefined // eventID matches webhook CAPI eventId for dedup
        );

        setOrderProcessed(true);
      } catch {
        setOrderProcessed(true);
      }
    };

    processOrder();
  }, [orderId, session_id, payment_intent, consent.marketing]);

  // Clear cart after order is processed
  useEffect(() => {
    if (orderProcessed) {
      const timer = setTimeout(() => clearCart(), 1500);
      return () => clearTimeout(timer);
    }
  }, [orderProcessed, clearCart]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-[min(80vw,1400px)]">
      <div className="bg-brand-sand dark:bg-opacity-0 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-brand-charcoal rounded-lg shadow-lg p-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-green-500 text-5xl mb-6"
            />
            <h1 className="text-3xl font-bold text-brand-dark dark:text-white mb-4">
              {t('success.thankYou')}
            </h1>
            <p className="text-gray-600 mb-6 dark:text-white">
              {t('success.confirmationEmail')}
            </p>
            <Link
              to={localePath('/shop')}
              className="inline-block bg-brand-blue text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              {t('success.continueShopping')}
            </Link>
          </motion.div>
        </div>
        <SEO
          title={t('seo.success.title')}
          description={t('seo.success.description')}
          noindex={true}
        />
      </div>
    </div>
  );
};

export default Success;